import { dateRange, degree, escapeHtml, fullName, locationText, mdToHtml, type TailoredResume } from "./markdown";

export const meta = { id: "minimal", name: "Minimal" };

const CSS = `
  :root { --ink: #0e0e0f; --muted: #1c1c1c; --faint: #3f3f3f; --rule: #c8c8c8; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #efeff1; color: var(--ink);
    font-family: "Libre Franklin", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    font-size: 12.5px; line-height: 1.4; }
  .page { max-width: 8.5in; margin: 24px auto; background: #fff; padding: 0.85in 0.85in; }
  header { margin-bottom: 26px; text-align: center; }
  h1 { font-size: 26px; font-weight: 700; margin: 0; }
  .title { font-size: 15px; color: var(--muted); margin: 5px 0 0; font-weight: 500; }
  .contact { font-size: 12px; color: var(--muted); margin-top: 12px; }
  section { margin-top: 22px; }
  h2 { font-size: 12.5px; font-weight: 700; text-transform: uppercase;
    color: var(--ink); margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1.5px solid var(--ink); }
  .body p { margin: 0 0 7px; }
  .body ul { margin: 5px 0 8px; padding-left: 15px; }
  .body li { margin: 3px 0; padding-left: 2px; }
  .entry { margin-bottom: 16px; }
  .entry:last-child { margin-bottom: 0; }
  .head { display: flex; justify-content: space-between; gap: 14px; align-items: baseline; }
  .role { font-size: 13.5px; font-weight: 700; }
  .role .org { font-weight: 500; color: var(--muted); }
  .dates { font-size: 11px; color: var(--faint); white-space: nowrap; }
  .place { font-size: 11px; color: var(--faint); margin: 1px 0 4px; }
  .rel { font-size: 11px; color: var(--muted); margin-top: 3px; }
  .rel .label { color: var(--faint); }
  .rel ul { display: inline; margin: 0; padding: 0; }
  .rel li { display: inline; }
  .rel li:not(:last-child)::after { content: ", "; }
  .inline { color: var(--muted); }
  .ref { margin-bottom: 9px; }
  .ref .name { font-weight: 600; }
  .ref .sub { color: var(--muted); }
  .ref .meta { font-size: 9.75px; color: var(--faint); }
  @media print {
    body { background: #fff; }
    .page { margin: 0; max-width: none; padding: 0.55in 0.6in; }
  }
`;

const has = (v: unknown) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
const section = (title: string, inner: string) =>
	has(inner) ? `<section><h2>${title}</h2><div class="body">${inner}</div></section>` : "";

function header(r: TailoredResume) {
	const c = (r.contact || {}) as TailoredResume["contact"];
	const bits = [c.email, c.phone, locationText(c.location)].filter(Boolean).map(escapeHtml).join(" | ");
	return `<header>
      <h1>${escapeHtml(fullName(c))}</h1>
      ${c.title ? `<div class="title">${escapeHtml(c.title)}</div>` : ""}
      ${bits ? `<div class="contact">${bits}</div>` : ""}
    </header>`;
}

const experience = (list: any[] | undefined) =>
	(list || [])
		.map(
			(j) => `<div class="entry">
      <div class="head"><span class="role">${escapeHtml(j.jobTitle || "")}${j.employer ? `<span class="org"> · ${escapeHtml(j.employer)}</span>` : ""}</span><span class="dates">${escapeHtml(dateRange(j))}</span></div>
      ${locationText(j.location) ? `<div class="place">${escapeHtml(locationText(j.location))}</div>` : ""}
      ${has(j.highlights) ? mdToHtml(j.highlights) : ""}
      ${has(j.relevantSkills) ? `<div class="rel"><span class="label">Relevant skills:</span> ${mdToHtml(j.relevantSkills)}</div>` : ""}
    </div>`,
		)
		.join("");

const education = (list: any[] | undefined) =>
	(list || [])
		.map(
			(ed) => `<div class="entry">
      <div class="head"><span class="role">${escapeHtml(degree(ed))}${ed.schoolName ? `<span class="org"> · ${escapeHtml(ed.schoolName)}</span>` : ""}</span><span class="dates">${escapeHtml(ed.graduation || "")}</span></div>
      ${locationText(ed.location) ? `<div class="place">${escapeHtml(locationText(ed.location))}</div>` : ""}
      ${has(ed.details) ? mdToHtml(ed.details) : ""}
    </div>`,
		)
		.join("");

const languages = (list: any[] | undefined) =>
	`<div class="inline">${(list || []).map((l) => `${escapeHtml(l.language)}${l.proficiency ? ` (${escapeHtml(l.proficiency)})` : ""}`).join("  ·  ")}</div>`;

const references = (list: any[] | undefined) =>
	(list || [])
		.map(
			(rf) => `<div class="ref">
      <span class="name">${escapeHtml(rf.name || "")}</span><span class="sub">${rf.designation ? `, ${escapeHtml(rf.designation)}` : ""}${rf.workPlace ? ` · ${escapeHtml(rf.workPlace)}` : ""}</span>
      ${[rf.phone, rf.email].filter(Boolean).length ? `<div class="meta">${[rf.phone, rf.email].filter(Boolean).map(escapeHtml).join("  ·  ")}</div>` : ""}
    </div>`,
		)
		.join("");

const customSections = (list: any[] | undefined) =>
	(list || []).map((s) => section(escapeHtml(s.title), has(s.content) ? mdToHtml(s.content) : "")).join("");

const linksList = (list: any[] | undefined) =>
	(list || []).map((l) => `<div>${l.type ? escapeHtml(l.type) + ": " : ""}${escapeHtml(l.value)}</div>`).join("");

export function renderBody(r: TailoredResume): string {
	return [
		header(r),
		section("Summary", has(r.summary) ? mdToHtml(r.summary) : ""),
		section("Skills", has(r.skills) ? mdToHtml(r.skills) : ""),
		section("Experience", experience(r.workExperience)),
		section("Education", education(r.education)),
		section("Certifications", has(r.certifications) ? mdToHtml(r.certifications) : ""),
		customSections(r.customSections),
		section("Accomplishments", has(r.accomplishments) ? mdToHtml(r.accomplishments) : ""),
		section("Affiliations", has(r.affiliations) ? mdToHtml(r.affiliations) : ""),
		section("Languages", has(r.languages) ? languages(r.languages) : ""),
		section("Additional Information", has(r.additionalInfo) ? mdToHtml(r.additionalInfo) : ""),
		section("Links", has(r.personalLinks) ? linksList(r.personalLinks) : ""),
		section("References", references(r.references)),
	].join("");
}

export function renderDocument(resume: TailoredResume): string {
	return `<!doctype html><html><head><meta charset="utf-8" /><style>${CSS}</style><link rel="preconnect" href="https://fonts.googleapis.com" /><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin /><link href="https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;500;600;700&display=swap" rel="stylesheet" /></head><body><div class="page" id="resume">${renderBody(resume)}</div></body></html>`;
}
